import type { ReactNode } from "react";
import type { components } from "../api/schema";
import { cx, type BadgeKind } from "./basics";

export type TestResult = components["schemas"]["TestResult"];

/** The label and colour of each test result (docs/test-suite.md section 2.6). The staff app and
    the portal share this chip. */
export const TEST_RESULTS: Record<TestResult, { label: string; tone: BadgeKind }> = {
  not_run: { label: "Not run", tone: "neutral" },
  pass: { label: "Pass", tone: "ok" },
  fail: { label: "Fail", tone: "bad" },
  blocked: { label: "Blocked", tone: "warn" },
  retest: { label: "Retest", tone: "info" },
  untestable: { label: "Untestable", tone: "mute" },
  waiting: { label: "Waiting", tone: "mute" },
  manual: { label: "Manual", tone: "purple" },
  functionality_changed: { label: "Functionality Changed", tone: "high" },
  waived: { label: "Waived", tone: "faint" },
  na: { label: "N/A", tone: "mute" },
};

export const testResultLabel = (result: TestResult) => TEST_RESULTS[result].label;

/** What each result means, in plain words for testers (docs/test-suite.md section 2.6). Shown as
    the tooltip of every result chip, button and filter option, and as the hint under the buttons
    where a result is picked (touch screens have no hover). */
export const RESULT_HELP: Record<TestResult, string> = {
  not_run: "Not tested yet in this environment.",
  pass: "Every part of the expected result held.",
  fail: "The app did something else than expected. A bug is usually logged for it.",
  blocked: "Couldn't run: an existing bug, the environment, an account or a prerequisite stops this case.",
  retest: "A fix is ready; run it again.",
  untestable: "Not on staging yet, so it can't be tested. No bug.",
  waiting: "Waiting on setup or data before it can be tested.",
  manual:
    "Needs a person: an AI tester can't do it (a CAPTCHA, a real inbox, a physical device, a judgement call).",
  functionality_changed:
    "The app now works differently on purpose; the case is out of date and needs rewriting.",
  waived: "Its bug was closed as Won't fix. Counts as done and stays visible as a known gap.",
  na: "Doesn't apply here.",
};

export const testResultHelp = (result: TestResult) => RESULT_HELP[result];

/** A result chip. `bug` (a bug key) shows after the label, for a Fail or Blocked result that has one. */
export function TestResultChip({
  result,
  label,
  bug,
  prefix,
  title,
}: {
  result: TestResult;
  label?: string;
  bug?: string | null;
  /** Shown before the label (an environment's short code, a count). */
  prefix?: ReactNode;
  /** Tooltip text before the explanation (an environment's code, who it is assigned to). */
  title?: string;
}) {
  const { label: name, tone } = TEST_RESULTS[result];
  return (
    <span
      className={cx("badge", `b-${tone}`)}
      data-result={result}
      title={`${title ? `${title}: ` : ""}${name}: ${RESULT_HELP[result]}`}
    >
      {prefix}
      {label ?? name}
      {bug ? <span className="mono"> · {bug}</span> : null}
    </span>
  );
}
